import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, googleProvider, firestore } from '../firebase';
import { signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import './LandingPage.css'; // Import your styles
import Nav from './Nav.js';
import Footer from './Footer.js';
import ZoadicSign from './ZoadicSign.js';
import ReactGA from 'react-ga4';
import InstallPWA from './InstallPWA.js';
import Calendar from './Calender.js';
import Loader from './Loader.js';

const LandingPage = () => {   
  const [buttonLoading, setButtonLoading] = useState(false);
  const navigate = useNavigate();
  const words = ['life choices', 'career paths', 'finance', 'marriage details'];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(localStorage.getItem('userId'));
  const [isLoading, setIsLoading] = useState(true); // Loading state
  const [platform, setPlatform] = useState('Web');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallSection, setShowInstallSection] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (userId) {
      ReactGA.set({ user_id: userId }); // Sets globally
    }
  }, [userId]);  

  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      if (userId) {
        try {
          const userRef = doc(firestore, 'users', userId);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            setUser(userDoc.data());
          } else {
            setUser(null);
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setIsLoading(false);
    };
    fetchUserData();
  }, [userId]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentWordIndex((prevIndex) => (prevIndex + 1) % words.length);
    }, 2000);

    return () => clearInterval(intervalId);
  }, [words.length]);
  
  useEffect(() => {
      const detectPlatform = () => {
        const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        return isPWA ? 'PWA' : 'Web';
      };
      setPlatform(detectPlatform());
  }, []);
  
  // Enhanced PWA detection with immediate feedback
  useEffect(() => {
    // Check for iOS devices
    setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent) || 
            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

    // Immediate PWA check
    const isRunningAsPWA = () => {
      return window.matchMedia('(display-mode: standalone)').matches || 
             window.navigator.standalone || 
             document.referrer.includes('android-app://');
    };

    // Initial check
    if (!isRunningAsPWA()) {
      setShowInstallSection(true);
    }

    const beforeInstallHandler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallSection(true);
      localStorage.removeItem('installDismissed');
    };

    const installedHandler = () => {
      setShowInstallSection(false);
      localStorage.setItem('installDismissed', 'true');
    };

    window.addEventListener('beforeinstallprompt', beforeInstallHandler);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstallHandler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const handleInstall = async () => {
    setIsInstalling(true);
    
    try {
      if (deferredPrompt) {
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        
        if (choice.outcome === 'accepted') {
          trackEvent(`Install Clicked - Landing Page`, `Install Clicked (Home Page)`, 'App side menu');
          console.log('User installed the app');
        } else {
          console.log('User dismissed the install prompt');
        }
        setDeferredPrompt(null);
      } else if (isIOS) {
        // iOS specific instructions
        alert(`To install:
1. Tap the Share icon 
2. Select "Add to Home Screen"
3. Tap "Add" in the top right`);
      } else {
        // Android/Desktop fallback
        alert('Look for "Install" or "Add to Home Screen" in your browser menu');
      }
    } catch (error) {
      console.error('Installation error:', error);
      trackEvent('Install Error', error.message, 'Home Page');
    } finally {
      setIsInstalling(false);
    }
  };

  const getPlatform = () => {
    if (window.matchMedia('(display-mode: standalone').matches || window.navigator.standalone) {
      return 'PWA';
    }
    return 'Website';
  };

  const trackEvent = (category, action, label = '') => {
    // Function is depricated
    // ReactGA.event({
    //   category,
    //   action: action + " - " + getPlatform(),
    //   label,
    //   platform: getPlatform(),
    // });
  };

  const trackGA4Event = (eventName, params = {}) => {
    ReactGA.event(eventName, {
      platform: getPlatform(), // optional, if you want
      ...params,
    });
  };

  const handleChatNowClick = () => {
    trackEvent(`User Onboarding`, `Chat Initiated`, 'Home Page Primary CTA');
    trackGA4Event('chat_initiated', {
      source: 'home_page',
      mode: 'personal',
    });
    navigate('/chat')
  };

  const navigateToChat = (mode) => {
    if (mode === 'relationship') {
      trackEvent(`Relationship Chat Clicked - Top Widget`, `Relationship Chat Clicked (Top Widget)`, 'Home screen widget section');
      trackGA4Event('chat_initiated', {
        source: 'home_page',
        mode: mode
      });
    }
    if (mode === 'personal') {  
      trackEvent(`Personal Chat Clicked - Top Widget`, `Personal Chat Clicked (Top Widget)`, 'Home screen widget section');
      trackGA4Event('chat_initiated', {
        source: 'home_page',
        mode: mode
      });
    }
    navigate('/chat'+ (mode ? `?mode=${mode}` : ''));
  }

  const navigateToMiniReports = () => {
    trackGA4Event('mini_reports_clicked', {
      source: 'home_page' 
    });
    navigate('/mini-reports');
  }

  const navigateToHoroscope = () => {
    trackEvent(`Horoscope Clicked - Top Widget`, `Horoscope Clicked (Top Widget)`, 'Home screen widget section');
    trackGA4Event('view_horoscope_clicked', {
      source: 'home_page',
      zodiacSign: user?.astro_data?.astro_details?.ascendant || 'unknown',
    });
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = (today.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed
    const dd = today.getDate().toString().padStart(2, '0');
    const formattedDate = `${yyyy}-${mm}-${dd}`;
  
    navigate(`/horoscope/${user.astro_data.astro_details.ascendant}/${formattedDate}/daily-horoscope`);
  };
  

  const navigateToPanchang = () => {
    trackEvent(`Panchang Clicked - Top Widget`, `Panchang Clicked (Top Widget)`, 'Home screen widget section' );
    trackGA4Event('view_panchang_clicked', {
      source: 'home_page'
    });
    const panchangSection = document.getElementById('panchang-section');
    if (panchangSection) {
      panchangSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  if (isLoading) {
    return (
      <Loader />
    );
  }

  return (
    <div>
      <Nav />
      {!userId && !user && (
        <section className="hero">
          <div className="hero-content">
            <h1>
              Get answers to all your questions about{' '}
              <span className="changing-word">{words[currentWordIndex]}</span>
            </h1>
            <p>Get personalized astrological readings and guidance based on Vedic astrology.</p>
            <button
              onClick={handleChatNowClick}
              className={`hover-filled-slide-right ${buttonLoading ? 'loading' : ''}`}
              disabled={buttonLoading}
            >
              {buttonLoading ? (
                <div className="spinner">
                  <div className="spin-dot"></div>
                </div>
              ) : (
                'Start Free Chat'
              )}
            </button><br />
            {/* <Link className="link-style" to="authentication">Create an account to continue →</Link> */}
          </div>
          <img src="astro.png" alt="Astrologer" className="hero-image" />
        </section>
      )}
      
      {userId && user && (
        <div className="welcome-back-container">
          <div className="welcome-back-header">
          <h2>
            <img 
              width="35px" 
              src='https://images.emojiterra.com/google/noto-emoji/unicode-15/color/512px/1f44b.png' 
              className="waving-hand" 
              alt="Waving hand"
            /> 
            Welcome back, {user.name}!
          </h2>
          </div>  
        
          <div className="action-buttons">
            <div className="action-column">
              <div className="action-button" onClick={()=> navigateToChat('personal')}>
                <span className="action-icon"><img src='https://www.astrosage.com/images/varta/chat.png' alt='chat' width="40px"></img></span>
                <span className="action-text"> Personal Chat</span>
              </div>
              <div className="action-button" onClick={()=> navigateToChat('love')}>
                <span className="action-icon"><img src="/ic_heart.png" alt='panchang' width="40px"></img></span>
                <span className="action-text">Marriage Chat</span>
              </div>
            </div>
          </div>
          <div className="action-buttons">
            <div className="action-column">
              <div className="action-button" onClick={()=> navigateToPanchang()}>
                <span className="action-icon"><img src="https://www.astrosage.com/images/varta/panchang.png" alt='panchang' width="40px"></img></span>
                <span className="action-text">Today's Panchang</span>
              </div>
              <div className="action-button" onClick={()=> navigateToHoroscope()}>
                <span className="action-icon"><img src='https://www.astrosage.com/images/varta/horoscope.png' alt='horoscope' width="40px"></img></span>
                <span className="action-text">View Horoscope</span>
              </div>
            </div>
          </div>
          <div className="action-buttons">
            <div className="action-column">
              <div className="action-button" onClick={()=> navigateToMiniReports()}>
                <span className="action-icon"><img src="https://www.astrosage.com/images/icons/ic_occult.png" alt='panchang' width="40px"></img></span>
                <span className="action-text">Mini Reports</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='section-2'>
        <img src='https://png.pngtree.com/png-vector/20220619/ourmid/pngtree-zodiac-icon-vector-illustration-design-png-image_5230956.png' alt='.bg'></img>
        <h1>Ultimate Astrology Platform.</h1>
        <p>Discover personalized Kundali readings, astrology insights, and tailored guidance for life, career, and health</p>
        <div className='card-container'>
          <div className="gradient-card">
            <h2 className="card-title">Kundali Reading</h2>
            <p className="card-content">Unlock personalized insights with detailed Kundali readings, offering guidance on life, relationships, career, and health, based on Vedic astrology.</p>
          </div>
          <div className="gradient-card">
            <h2 className="card-title">Daily horoscope</h2>
            <p className="card-content">Unlock personalized insights with detailed Kundali readings, offering guidance on life, relationships, career, and health, based on Vedic astrology.</p>
          </div>
          <div className="gradient-card">
            <h2 className="card-title">Chat with Astrologer</h2>
            <p className="card-content">Unlock personalized insights with detailed Kundali readings, offering guidance on life, relationships, career, and health, based on Vedic astrology.</p>
          </div>
        </div>
      </div>
      <div className='section-calender'>
        <h1 id="panchang-section">Panchang</h1>
        <Calendar /> 
      </div>
      
      {false && (
        <div className='section-4'>
        <div className="app-install-container">
          <div className="app-install-content">
            <h2>Install app & get <span style={{color: "#FD6805"}}>10 free chats everyday</span></h2>
            
            <div className="benefits-list">
              <div className="benefit-item">
                <span className="checkmark">✓</span>
                <span>1-on-1 chat with expert astrologer</span>
              </div>
              <div className="benefit-item">
                <span className="checkmark">✓</span>
                <span>Personalized Kundli analysis</span>
              </div>
              <div className="benefit-item">
                <span className="checkmark">✓</span>
                <span>Daily horoscope alerts</span>
              </div>
              <div className="benefit-item">
                <span className="checkmark">✓</span>
                <span>Accurate love and career predictions</span>
              </div>
            </div>
  
            <div className="stats">
              <strong>10 lakh+</strong> chats completed! 
            </div>
  
            <button 
              className="install-button" 
              onClick={handleInstall}
              disabled={isInstalling}
            >
              {isInstalling ? (
                <>
                  <span className="spinner"></span> 
                </>
              ) : (
                'Install Now'
              )}
            </button>
          </div>
  
          <div className="app-image-container">
            <img 
              src="/app-install-mobile.png" 
              alt="Our mobile app" 
              className="app-image"
              loading="lazy"
            />
          </div>
        </div>
        </div>
      )}
      
      <div className='section-3'>
        <h1>Get Your Horoscope Today</h1>
        <p>Your daily horoscope brings clarity to love, ambition, and wellness, helping you find your best path.</p>
        <ZoadicSign />
      </div>  
      {/* <InstallPWA /> */}
      <Footer />
    </div>
  );
};

export default LandingPage;